import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Truck, 
  Receipt, 
  Calendar, 
  MessageSquare, 
  LogOut,
  Menu,
  X,
  Users,
  BarChart3,
  Wrench,
  MapPin,
  Building2,
  FileText,
  Clock,
  AlertTriangle,
  Package,
  Webhook
} from 'lucide-react';
import GPSTracker from './GPSTracker';
import NotificationCenter from './NotificationCenter';
import LanguageSwitcher from './LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import cashxService from '@/services/cashxService';

const Layout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [branding, setBranding] = useState({
    restaurantName: null,
    logoUrl: null,
    logoBase64: null,
    primaryColor: null
  });

  // Load branding on mount
  useEffect(() => {
    const loadBranding = () => {
      const storedBranding = cashxService.getBranding();
      setBranding(storedBranding);
      
      // Apply CSS variables
      cashxService.applyBranding();
    };
    
    loadBranding();
  }, []);

  const loadFeatures = async () => {
    try {
      const res = await axios.get('/settings/features');
      setEnabledFeatures(res.data);
    } catch (error) {
      console.error('Error loading features:', error);
      // Default to all enabled if error
      setEnabledFeatures({
        vacation_management: true,
        payroll_documents: true,
        time_tracking: true,
        chat: true,
        gps_tracking: true
      });
    }
  };

  useEffect(() => {
    loadFeatures();
  }, []);

  const allDriverNavigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard, alwaysShow: true },
    { name: 'Lieferungen', href: '/deliveries', icon: Package, alwaysShow: true },
    { name: t('nav.vehicles'), href: '/vehicles', icon: Truck, feature: 'gps_tracking' },
    { name: t('nav.reportDamage'), href: '/report-damage', icon: AlertTriangle, alwaysShow: true },
    { name: t('nav.receipts'), href: '/receipts', icon: Receipt, alwaysShow: true },
    { name: t('nav.schedule'), href: '/schedule', icon: Calendar, alwaysShow: true },
    { name: t('nav.vacation'), href: '/vacation', icon: Calendar, feature: 'vacation_management' },
    { name: t('nav.myPayroll'), href: '/my-payroll', icon: Receipt, feature: 'payroll_documents' },
    { name: t('nav.timeTracking'), href: '/time-tracking', icon: Clock, feature: 'time_tracking' },
    { name: t('nav.shiftPreferences'), href: '/shift-preferences', icon: Calendar, alwaysShow: true },
    { name: t('nav.chat'), href: '/chat', icon: MessageSquare, feature: 'chat' },
  ];

  // Filter navigation based on enabled features
  const driverNavigation = allDriverNavigation.filter(item => 
    item.alwaysShow || (item.feature && enabledFeatures[item.feature])
  );

  const adminNavigation = [
    { name: t('nav.dashboard'), href: '/admin', icon: LayoutDashboard },
    { name: t('nav.liveTracking'), href: '/admin/tracking', icon: MapPin },
    { name: t('nav.geofencing'), href: '/admin/geofencing', icon: MapPin },
    { name: t('nav.vehicles'), href: '/admin/vehicles', icon: Truck },
    { name: t('nav.staff'), href: '/admin/staff', icon: Users },
    { name: t('nav.schedule'), href: '/admin/schedules', icon: Calendar },
    { name: t('nav.receipts'), href: '/admin/receipts', icon: Receipt },
    { name: t('nav.vacation'), href: '/admin/vacation', icon: Calendar },
    { name: t('nav.payroll'), href: '/admin/payroll', icon: Receipt },
    { name: t('nav.timeTracking'), href: '/admin/time-tracking', icon: Clock },
    { name: t('nav.lexware'), href: '/admin/lexware', icon: BarChart3 },
    { name: t('nav.lexware') + ' Sync', href: '/admin/lexware-sync', icon: FileText },
    { name: t('nav.backup'), href: '/admin/backups', icon: LayoutDashboard },
    { name: t('nav.shiftPreferences'), href: '/admin/shift-preferences', icon: Calendar },
    { name: 'Wartung', href: '/admin/maintenance', icon: Wrench },
    { name: t('nav.analytics'), href: '/admin/analytics', icon: BarChart3 },
    { name: t('nav.billing'), href: '/admin/billing', icon: Receipt },
    { name: t('nav.settings'), href: '/admin/settings', icon: Wrench },
    { name: t('nav.chat'), href: '/chat', icon: MessageSquare },
  ];

  const superAdminNavigation = [
    { name: 'Dashboard', href: '/super-admin/overview', icon: LayoutDashboard },
    { name: 'Tenants', href: '/super-admin/tenants', icon: Building2 },
    { name: 'Webhooks', href: '/super-admin/webhooks', icon: Webhook },
    { name: 'Billing', href: '/super-admin/billing', icon: Receipt },
  ];

  const getNavigation = () => {
    if (user?.role === 'super_admin') return superAdminNavigation;
    if (user?.role === 'admin') return adminNavigation;
    return driverNavigation;
  };

  const navigation = getNavigation();

  const handleLogout = async () => {
    try {
      // First logout from Cash-X (clear tokens)
      const cashxService = await import('@/services/cashxService').then(m => m.default);
      await cashxService.logout();
      
      // Try Flotto Backend logout (may fail if only Cash-X session exists)
      try {
        await axios.post('/auth/logout');
      } catch (flottoLogoutError) {
        // Ignore Flotto logout errors - user may only have Cash-X session
        console.log('Flotto logout skipped (Cash-X only session)');
      }
      
      toast.success(t('auth.logoutSuccess'));
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate away even if logout had issues
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-zinc-900 border-r border-zinc-800">
        <div className="p-6 border-b border-zinc-800">
          {/* Flotto Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/flotto-logo.png"
              alt="Flotto"
              className="h-14 w-14 object-contain"
            />
            <div>
              <h2 className="font-bold text-xl text-white">
                {user?.role === 'super_admin' ? 'Flotto SaaS' : (branding.restaurantName || 'Flotto')}
              </h2>
              <p className="text-xs text-zinc-400">{user?.email}</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive 
                    ? 'bg-primary text-white' 
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 capitalize">{user?.role}</div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Abmelden
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900 border-b border-zinc-800 safe-area-top">
        <div className="flex items-center justify-between p-3 sm:p-4">
          {/* Mobile Logo - use synced branding or fallback */}
          {branding.logoUrl || branding.logoBase64 ? (
            <div className="flex items-center gap-2">
              <img 
                src={branding.logoBase64 || branding.logoUrl}
                alt={branding.restaurantName || 'Restaurant Logo'}
                className="h-8 sm:h-10 w-auto max-w-[120px] object-contain"
                onError={(e) => {
                  e.target.src = "https://customer-assets.emergentagent.com/job_70aa5bcf-098c-4523-b0d6-5bbb87ecc869/artifacts/ysjl1nto_IMG_8154.jpeg";
                }}
              />
              {branding.restaurantName && (
                <span className="text-xs sm:text-sm font-medium text-zinc-400 hidden sm:inline">{branding.restaurantName}</span>
              )}
            </div>
          ) : (
            <img 
              src="https://customer-assets.emergentagent.com/job_70aa5bcf-098c-4523-b0d6-5bbb87ecc869/artifacts/ysjl1nto_IMG_8154.jpeg"
              alt="ZOZO Logo"
              className="h-10 sm:h-12"
            />
          )}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher />
            <NotificationCenter />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden h-9 w-9 sm:h-10 sm:w-10"
            >
              {mobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-zinc-900 border-b border-zinc-800 p-3 sm:p-4 max-h-[70vh] overflow-y-auto">
            <nav className="space-y-1 sm:space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md transition-colors ${
                      isActive 
                        ? 'bg-primary text-white' 
                        : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                    }`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="font-medium text-sm sm:text-base">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-zinc-800">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-zinc-400 h-10 sm:h-auto"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span className="text-sm sm:text-base">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 mt-16 sm:mt-20 lg:mt-0">
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 xl:p-12">
          {children}
        </div>
      </main>
      
      {/* GPS Tracker (background service for drivers) */}
      <GPSTracker user={user} />
    </div>
  );
};

export default Layout;
